import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import Footer from '../components/Footer';
import '../style.css';
import '../nov.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      if (!db) {
        console.log('[local dev] Contact form skipped:', formData);
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitStatus(null), 5000);
        return;
      }

      await addDoc(collection(db, 'contact-messages'), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        timestamp: serverTimestamp(),
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // 5 saniye sonra success mesajını temizle
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);

    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus('error');
      
      // 5 saniye sonra error mesajını temizle
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="contact" className="min-h-screen bg-[#f5f5f7] py-32 lg:py-48 relative overflow-hidden">
        <div className="container mx-auto px-6 lg:px-16 max-w-[1400px] relative z-10">
          <div className="w-full flex flex-col lg:flex-row items-center lg:items-start justify-between gap-16 lg:gap-24">
          
          {/* Sol Taraf - Header */}
          <div className="lg:w-5/12 text-center lg:text-left space-y-10">
            <motion.div 
              className="inline-block"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span 
                className="text-xs font-medium badge-gradient text-gray-700 py-2.5 px-5 rounded-full inline-block uppercase tracking-wide" 
                style={{ fontFamily: 'aesthet-nova' }}
              >
                Get In Touch
              </span>
            </motion.div>

            <motion.h1 
              className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] tracking-tight uppercase"
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span style={{ color: 'rgb(70, 52, 135)' }}>
                HAVE
              </span>
              <br/>
              <span 
                style={{ '--shimmer-width': '250px' }} 
                className="text-neutral-600/50 animate-shimmer bg-clip-text bg-no-repeat [background-position:0_0] [background-size:var(--shimmer-width)_100%] [transition:background-position_1s_cubic-bezier(.6,.6,0,1)_infinite] bg-gradient-to-r from-neutral-100 via-black/80 via-50% to-neutral-100"
              >
                QUESTIONS?
              </span>
              <br/>
              <span style={{ color: 'rgb(70, 52, 135)' }}>
                CONTACT US!
              </span>
            </motion.h1>

            <motion.p 
              className="text-lg leading-relaxed text-gray-600"
              style={{ fontFamily: 'aesthet-nova' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              The Media Nov team is here to support you on your musical journey. Feel free to reach out with any questions or inquiries.
            </motion.p>
          </div>

          {/* Sağ Taraf - Form and Contact Info */}
          <div className="lg:w-7/12 flex flex-col gap-12">
            
            {/* Form */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <h3 className="text-2xl font-semibold mb-6 text-black text-center lg:text-left">Send Us a Message</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-3xl focus:outline-none focus:border-blue-500 transition-all duration-300"
                    />
                  </motion.div>
                  
                  <motion.div 
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.2 }}
                  >
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-3xl focus:outline-none focus:border-blue-500 transition-all duration-300"
                    />
                  </motion.div>
                </div>

                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-3xl focus:outline-none focus:border-blue-500 transition-all duration-300"
                  />
                </motion.div>

                <motion.div 
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.2 }}
                >
                  <textarea
                    name="message"
                    placeholder="Your Message"
                    required
                    rows="6"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-3xl focus:outline-none focus:border-blue-500 resize-vertical transition-all duration-300"
                  ></textarea>
                </motion.div>

                {/* Success/Error Messages */}
                {submitStatus === 'success' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-3xl"
                  >
                    ✅ Your message has been sent successfully! We'll get back to you soon.
                  </motion.div>
                )}
                
                {submitStatus === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-3xl"
                  >
                    ❌ There was an error sending your message. Please try again.
                  </motion.div>
                )}

                <motion.div 
                  whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full font-semibold py-4 px-6 rounded-3xl transition-all duration-300 bg-gradient-to-r from-[#9BCFFF] via-[#FCD4FE] to-[#B6F1F6] text-black disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </motion.div>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-2xl font-semibold mb-6 text-black text-center lg:text-left">Contact Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              
              {/* Media Nov Inc. */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-black">Media Nov Inc.</h4>
                <ul className="space-y-4 text-gray-600">
                  <motion.li 
                    className="flex items-center"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <a href="mailto:contact@medianov.com" className="hover:underline transition-all duration-300">
                      contact@medianov.com
                    </a>
                  </motion.li>

                  <motion.li 
                    className="flex items-center"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    <div className="flex flex-col">
                      <a href="tel:+12133295216" className="hover:underline transition-all duration-300">US: +1 (213) 329-5216</a>
                      <a href="tel:+447438395244" className="hover:underline transition-all duration-300">Global: +44 7438 395244</a>
                    </div>
                  </motion.li>

                  <motion.li 
                    className="flex items-start"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span className="transition-all duration-300">
                      13700 Marina Pointe Drive Unit 624<br/>Marina Del Rey 90292 CA, USA
                    </span>
                  </motion.li>
                </ul>
              </div>

              {/* Media Nov Records Ltd */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-black">Media Nov Records Ltd</h4>
                <ul className="space-y-4 text-gray-600">
                  <motion.li 
                    className="flex items-center"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <a href="mailto:records@medianov.com" className="hover:underline transition-all duration-300">
                      records@medianov.com
                    </a>
                  </motion.li>

                  <motion.li 
                    className="flex items-center"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    <div className="flex flex-col">
                      <a href="tel:+12133295216" className="hover:underline transition-all duration-300">US: +1 (213) 329-5216</a>
                      <a href="tel:+447438395244" className="hover:underline transition-all duration-300">Global: +44 7438 395244</a>
                    </div>
                  </motion.li>

                  <motion.li 
                    className="flex items-start"
                    whileHover={{ scale: 1.05, x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 inline-block mr-2 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span className="transition-all duration-300">
                      The Black Church,<br/>St. Mary's Place, Dublin 7<br/>D07 P4AX, Ireland
                    </span>
                  </motion.li>
                </ul>
              </div>
              
              </div>
            </motion.div>

          </div>
          
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default Contact;
